import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Alert, Snackbar } from '@mui/material';
import { GetApp, Update, Wifi, WifiOff } from '@mui/icons-material';

interface PWAInstallProps {
  onInstallSuccess?: () => void;
}

const PWAInstall: React.FC<PWAInstallProps> = ({ onInstallSuccess }) => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [notification, setNotification] = useState<{ message: string; severity: 'success' | 'error' | 'info' } | null>(null);

  useEffect(() => {
    // Check if app is already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isFullscreen = window.matchMedia('(display-mode: fullscreen)').matches;
    const isMinimalUI = window.matchMedia('(display-mode: minimal-ui)').matches;
    
    setIsInstalled(isStandalone || isFullscreen || isMinimalUI || 
      (window.navigator as any).standalone === true);

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      console.log('[PWA] Install prompt available');
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      console.log('[PWA] App installed');
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
      setNotification({ message: 'App installed successfully!', severity: 'success' });
      onInstallSuccess?.();
    };

    // Listen for online/offline events
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    // Register service worker
    const registerServiceWorker = async () => {
      if ('serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.register('/sw-tablet.js');
          console.log('[PWA] Service worker registered:', registration);

          // Listen for updates
          registration.addEventListener('updatefound', () => {
            console.log('[PWA] Update available');
            setUpdateAvailable(true);
          });

          // Check for updates
          registration.update();
        } catch (error) {
          console.error('[PWA] Service worker registration failed:', error);
        }
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    registerServiceWorker();

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [onInstallSuccess]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('[PWA] User accepted install');
        setNotification({ message: 'Installing app...', severity: 'info' });
      } else {
        console.log('[PWA] User declined install');
        setNotification({ message: 'Installation cancelled', severity: 'info' });
      }
      
      setDeferredPrompt(null);
      setIsInstallable(false);
    } catch (error) {
      console.error('[PWA] Install failed:', error);
      setNotification({ message: 'Installation failed', severity: 'error' });
    }
  };

  const handleUpdateClick = () => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then((registration) => {
        if (registration?.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          window.location.reload();
        }
      });
    }
  };

  const addToHomeScreen = () => {
    setNotification({ 
      message: 'To install: Tap the browser menu and select "Add to Home Screen"', 
      severity: 'info' 
    });
  };

  return (
    <Box>
      {/* Install Prompt */}
      {isInstallable && !isInstalled && (
        <Box sx={{ mb: 2, p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            📱 Install Tablet App
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Install this app for a better tablet experience with offline support and camera access.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleInstallClick}
            startIcon={<GetApp />}
            sx={{ mr: 1 }}
          >
            Install App
          </Button>
          <Button
            variant="outlined"
            onClick={addToHomeScreen}
            size="small"
          >
            Manual Install
          </Button>
        </Box>
      )}

      {/* Update Available */}
      {updateAvailable && (
        <Box sx={{ mb: 2, p: 2, backgroundColor: '#fff3e0', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            🔄 Update Available
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            A new version of the app is available with improved features.
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleUpdateClick}
            startIcon={<Update />}
          >
            Update Now
          </Button>
        </Box>
      )}

      {/* App Status */}
      {isInstalled && (
        <Box sx={{ mb: 2, p: 2, backgroundColor: '#e8f5e8', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
            ✅ App installed successfully
            {isOnline ? (
              <Wifi sx={{ ml: 1, color: 'green' }} />
            ) : (
              <WifiOff sx={{ ml: 1, color: 'orange' }} />
            )}
            <Typography variant="caption" sx={{ ml: 1 }}>
              {isOnline ? 'Online' : 'Offline Mode'}
            </Typography>
          </Typography>
        </Box>
      )}

      {/* Offline Warning */}
      {!isOnline && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You're offline. Some features may be limited. Photos will be uploaded when connection is restored.
        </Alert>
      )}

      {/* Notification Snackbar */}
      <Snackbar
        open={notification !== null}
        autoHideDuration={6000}
        onClose={() => setNotification(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setNotification(null)} 
          severity={notification?.severity || 'info'} 
          sx={{ width: '100%' }}
        >
          {notification?.message || ''}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PWAInstall;
